import axios from "axios";

export const DEFAULT_SETTINGS = {
  riskAppetite: "Moderate",
  criticalExposureThreshold: 5000000,
  boardEscalationThreshold: "Critical",
  delayedInitiativesThreshold: 5,

  simulations: 50000,
  confidenceLevel: "P95",
  currency: "USD",
  lossModel: "Triangular",

  defaultDashboard: "Executive",
  presentationMode: false,
  refreshFrequency: "Daily",

  organizationName: "PHD Consulting",
  reportTheme: "Executive Dark",

  aiGuidance: true,
  executiveInterpretation: true,
  governanceInsights: true,

  seedMode: "Fixed",
  seedValue: 42,

  riskAppetiteP95: 5000000,
  riskAppetiteEAL: 1500000,

  capitalEfficiencyPolicyId: null,
  capitalEfficiencyPolicyVersion: null,
  marginalCapitalEfficiencyHurdle: null,
  capitalEfficiencyMandatoryAppetiteDimensions: [],

  enable_correlation_modeling: false,
  shared_dependency_failure_rate: 0.05,
  correlation_shock_min: 1.1,
  correlation_shock_mode: 1.4,
  correlation_shock_max: 2.0,
  minimum_shared_dependency_count: 2,
};

const FAIR_SEED_MODE_ALIASES = Object.freeze({
  Fixed: "Fixed",
  Random: "Random",
  "Governance Mode": "Fixed",
  "Exploratory Mode": "Random",
});

export function getFairSeedConfig(settings = {}) {
  const suppliedMode = settings.seedMode;
  const seedMode = suppliedMode == null || suppliedMode === ""
    ? DEFAULT_SETTINGS.seedMode
    : FAIR_SEED_MODE_ALIASES[suppliedMode];

  // Existing malformed stored settings fail closed to governed reproducibility.
  // The API write boundary rejects these values; this warning makes stale local
  // data visible without silently changing a governed analysis to random mode.
  if (!seedMode) {
    console.error(`Unsupported FAIR seed mode "${suppliedMode}"; using Fixed mode.`);
  }

  const canonicalMode = seedMode || "Fixed";
  const parsedSeed = Number(settings.seedValue);
  const seedValue = Number.isInteger(parsedSeed) && parsedSeed > 0
    ? parsedSeed
    : DEFAULT_SETTINGS.seedValue;

  return {
    seedMode: canonicalMode,
    seedValue,
    seed: canonicalMode === "Random" ? null : seedValue,
  };
}

export function normalizePlatformSettings(data = {}) {
  const seedConfig = getFairSeedConfig(data);
  return {
    ...DEFAULT_SETTINGS,
    ...data,
    simulations:
      data.simulations ??
      data.monteCarloIterations ??
      DEFAULT_SETTINGS.simulations,
    seedMode: seedConfig.seedMode,
    seedValue: seedConfig.seedValue,
  };
}

export function getPlatformSettings() {
  try {
    const saved = localStorage.getItem("platformSettings");
    const parsed = saved ? JSON.parse(saved) : {};

    return normalizePlatformSettings(parsed);
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export async function fetchPlatformSettings() {
  const { data } = await axios.get("/api/digital-risk/platform-settings/");

  const normalized = normalizePlatformSettings(data);

  localStorage.setItem(
    "platformSettings",
    JSON.stringify(normalized)
  );

  return normalized;
}

export function getDefaultDashboardPath() {
  const settings = getPlatformSettings();

  switch (settings.defaultDashboard) {
    case "Board":
      return "/board-dashboard";

    case "Operational":
      return "/mitigation-tracker";

    case "Executive":
    default:
      return "/executive-dashboard";
  }
}
